import * as XLSX from 'xlsx';
import { adminService } from './adminService';
import { registrationService } from './registrationService';
import { supabase } from '../lib/supabase';

export const exportService = {
  // ============================================================================
  // ASYNC EXPORT REQUEST (New - Uses Edge Function + Pub/Sub + Cloud Function)
  // ============================================================================

  /**
   * Request an export asynchronously via Edge Function → Pub/Sub → Cloud Function
   * Returns immediately with exportHistoryId, processing happens in background
   * @param {string} exportType - Type of export (draws, apparel, player_details, etc.)
   * @param {string} format - Export format (Excel, CSV, PDF)
   * @param {object} filters - Filter criteria
   * @param {array} selectedPlayerIds - Optional array of player IDs for player_cards export
   * @returns {Promise<{success: boolean, exportHistoryId: string, status: string}>}
   */
  async requestExport(exportType, format, filters = {}, selectedPlayerIds = null) {
    try {
      const { data, error } = await supabase.functions.invoke('publish-export', {
        body: {
          exportType,
          format,
          filters,
          selectedPlayerIds
        }
      })

      if (error) {
        console.error('Error calling publish-export function:', error)
        throw new Error(`Failed to request export: ${error.message}`)
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to request export')
      }

      return {
        success: true,
        exportHistoryId: data.exportHistoryId,
        status: data.status,
        messageId: data.messageId
      }
    } catch (error) {
      console.error('Error requesting export:', error)
      throw error
    }
  },

  /**
   * Subscribe to export status updates via Supabase Realtime
   * @param {string} exportHistoryId - The export history record ID
   * @param {function} callback - Callback function called when status changes
   * @returns {object} Realtime subscription object (call .unsubscribe() to stop)
   */
  subscribeToExportStatus(exportHistoryId, callback) {
    const channel = supabase
      .channel(`export-${exportHistoryId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'export_history',
          filter: `id=eq.${exportHistoryId}`
        },
        (payload) => {
          callback(payload.new)
        }
      )
      .subscribe()

    return channel
  },

  // ============================================================================
  // EXPORT REGISTRATIONS (Legacy - kept for backward compatibility)
  // ============================================================================

  async exportRegistrations(format, filters = {}) {
    try {
      // Fetch ALL registrations with filters (use large pageSize for exports)
      const result = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
      const registrations = result?.data || [];

      // Transform data for export
      const exportData = registrations.map(reg => ({
        'Registration Number': reg.id || 'N/A',
        'School Name': reg.schoolName || 'N/A',
        'City': reg.city || 'N/A',
        'Age Category': reg.ageCategory || 'N/A',
        'Coach Name': reg.coachName || 'N/A',
        'Status': reg.status || 'N/A',
        'Submitted Date': reg.submittedDate || 'N/A',
        'Reviewer': reg.reviewer || 'Unassigned'
      }));

      let fileBlob;
      let fileName;
      const timestamp = new Date().toISOString().split('T')[0];

      if (format === 'CSV') {
        fileBlob = this.createCSVBlob(exportData, 'Registrations');
        fileName = `Registrations_${timestamp}.csv`;
      } else if (format === 'Excel') {
        fileBlob = this.createExcelBlob(exportData, 'Registrations');
        fileName = `Registrations_${timestamp}.xlsx`;
      } else if (format === 'PDF') {
        fileBlob = await this.createPDFBlob(exportData, 'Registrations');
        fileName = `Registrations_${timestamp}.pdf`;
      } else {
        throw new Error(`Unsupported format: ${format}`);
      }

      // Upload to storage (optional - don't fail if it fails)
      let filePath = null;
      let publicUrl = null;
      try {
        const uploadResult = await this.uploadExportFile(fileBlob, fileName, 'registration');
        filePath = uploadResult.filePath;
        publicUrl = uploadResult.publicUrl;
      } catch (uploadError) {
        console.warn('Failed to upload export file to storage:', uploadError);
        // Continue with download even if upload fails
      }

      // Trigger download
      const link = document.createElement('a');
      const url = URL.createObjectURL(fileBlob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return { filePath, fileName, publicUrl };
    } catch (error) {
      console.error('Error exporting registrations:', error);
      throw error;
    }
  },

  // ============================================================================
  // EXPORT PLAYERS
  // ============================================================================

  async exportPlayers(format, filters = {}) {
    try {
      // Fetch ALL registrations with filters (use large pageSize for exports)
      const result = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
      const registrations = result?.data || [];
      
      // Fetch players for each registration
      const allPlayers = [];
      for (const reg of registrations) {
        try {
          const players = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
          players?.forEach(player => {
            allPlayers.push({
              'Player Name': `${player.first_name} ${player.middle_name || ''} ${player.last_name}`.trim(),
              'Date of Birth': player.date_of_birth || 'N/A',
              'Age': this.calculateAge(player.date_of_birth),
              'MCA ID': player.mca_id || 'N/A',
              'Role': player.role?.replace('_', ' ') || 'Player',
              'Is Captain': player.is_captain ? 'Yes' : 'No',
              'School': reg.schoolName || 'N/A',
              'Team Category': reg.ageCategory || 'N/A',
              'City': reg.city || 'N/A',
              'Phone': player.mobile_number || 'N/A',
              'Email': player.email || 'N/A'
            });
          });
        } catch (err) {
          console.error(`Error fetching players for registration ${reg.id}:`, err);
        }
      }

      return await this.handleExportWithStorage(allPlayers, 'Players', 'players', format);
    } catch (error) {
      console.error('Error exporting players:', error);
      throw error;
    }
  },

  // ============================================================================
  // EXPORT SCHOOLS
  // ============================================================================

  async exportSchools(format, filters = {}) {
    try {
      // Fetch ALL registrations with filters (use large pageSize for exports)
      const result = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
      const registrations = result?.data || [];
      
      // Group by school
      const schoolMap = {};
      registrations.forEach(reg => {
        const schoolName = reg.schoolName || 'Unknown';
        if (!schoolMap[schoolName]) {
          schoolMap[schoolName] = {
            'School Name': schoolName,
            'City': reg.city || 'N/A',
            'Total Teams': 0,
            'Approved Teams': 0,
            'Pending Teams': 0,
            'Rejected Teams': 0
          };
        }
        schoolMap[schoolName]['Total Teams']++;
        if (reg.status === 'approved') schoolMap[schoolName]['Approved Teams']++;
        else if (reg.status === 'submitted' || reg.status === 'pending') schoolMap[schoolName]['Pending Teams']++;
        else if (reg.status === 'rejected') schoolMap[schoolName]['Rejected Teams']++;
      });

      const exportData = Object.values(schoolMap);

      return await this.handleExportWithStorage(exportData, 'Schools', 'schools', format);
    } catch (error) {
      console.error('Error exporting schools:', error);
      throw error;
    }
  },

  // ============================================================================
  // EXPORT DOCUMENTS
  // ============================================================================

  async exportDocuments(format, filters = {}) {
    try {
      // Fetch ALL registrations with filters (use large pageSize for exports)
      const result = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
      const registrations = result?.data || [];
      
      const allDocuments = [];
      for (const reg of registrations) {
        try {
          const players = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
          for (const player of players || []) {
            const docs = await registrationService.getPlayerDocuments(player.id);
            docs?.forEach(doc => {
              allDocuments.push({
                'Team': reg.schoolName || 'N/A',
                'Player Name': `${player.first_name} ${player.last_name}`.trim(),
                'Document Type': doc.document_type?.replace('_', ' ') || 'N/A',
                'Upload Date': doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-GB') : 'N/A',
                'File Size': doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(2)} MB` : 'N/A',
                'Status': 'Uploaded'
              });
            });
          }
        } catch (err) {
          console.error(`Error fetching documents for registration ${reg.id}:`, err);
        }
      }

      return await this.handleExportWithStorage(allDocuments, 'Documents', 'documents', format);
    } catch (error) {
      console.error('Error exporting documents:', error);
      throw error;
    }
  },

  // ============================================================================
  // CSV EXPORT
  // ============================================================================

  // Helper to create CSV blob
  createCSVBlob(data, filename) {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    // Get headers from first object
    const headers = Object.keys(data[0]);
    
    // Create CSV content
    let csvContent = headers.join(',') + '\n';
    
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header] || '';
        // Escape quotes and wrap in quotes if contains comma or newline
        if (typeof value === 'string' && (value.includes(',') || value.includes('\n') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      });
      csvContent += values.join(',') + '\n';
    });

    return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  },

  exportToCSV(data, filename) {
    try {
      const blob = this.createCSVBlob(data, filename);
      const fileName = `${filename}_${new Date().getTime()}.csv`;
      
      // Trigger download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert('No data to export');
    }
  },

  // ============================================================================
  // EXCEL EXPORT
  // ============================================================================

  // Helper to create Excel blob
  createExcelBlob(data, filename) {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    
    // Generate Excel file as blob
    const excelBlob = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBlob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  },

  exportToExcel(data, filename) {
    try {
      const blob = this.createExcelBlob(data, filename);
      const fileName = `${filename}_${new Date().getTime()}.xlsx`;
      
      // Trigger download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert('No data to export');
    }
  },

  // ============================================================================
  // PDF EXPORT
  // ============================================================================

  // Helper to create PDF blob
  async createPDFBlob(data, filename) {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    // Dynamically import jsPDF
    const jsPDF = (await import('jspdf')).default;
    const pdf = new jsPDF('l', 'mm', 'a4'); // landscape orientation for tables
    
    // Set font
    pdf.setFontSize(12);
    
    // Get headers
    const headers = Object.keys(data[0]);
    const colWidth = 270 / headers.length; // A4 landscape width is 297mm, use 270mm for margins
    const rowHeight = 7;
    const startY = 20;
    let y = startY;
    
    // Add title
    pdf.setFontSize(16);
    pdf.text(filename, 14, 15);
    pdf.setFontSize(10);
    
    // Add headers
    pdf.setFillColor(200, 200, 200);
    pdf.rect(14, y, colWidth * headers.length, rowHeight, 'F');
    pdf.setTextColor(0, 0, 0);
    headers.forEach((header, i) => {
      pdf.text(header.substring(0, 20), 14 + (i * colWidth), y + 5);
    });
    y += rowHeight;
    
    // Add data rows
    data.forEach((row, rowIndex) => {
      if (y > 190) { // New page if needed
        pdf.addPage();
        y = startY;
        // Redraw headers on new page
        pdf.setFillColor(200, 200, 200);
        pdf.rect(14, y, colWidth * headers.length, rowHeight, 'F');
        headers.forEach((header, i) => {
          pdf.text(header.substring(0, 20), 14 + (i * colWidth), y + 5);
        });
        y += rowHeight;
      }
      
      headers.forEach((header, i) => {
        const value = String(row[header] || '').substring(0, 20);
        pdf.text(value, 14 + (i * colWidth), y + 5);
      });
      y += rowHeight;
    });
    
    // Get PDF as blob
    const pdfBlob = pdf.output('blob');
    return pdfBlob;
  },

  async exportToPDF(data, filename) {
    try {
      const blob = await this.createPDFBlob(data, filename);
      const fileName = `${filename}_${new Date().getTime()}.pdf`;
      
      // Trigger download
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try CSV or Excel format.');
    }
  },

  // ============================================================================
  // EXPORT DRAWS
  // ============================================================================

  async exportDraws(format, filters = {}) {
    try {
      // Fetch ALL registrations with filters
      const result = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
      const registrations = result?.data || [];

      // Transform data for export
      let serialNumber = 1;
      const exportData = registrations.map(reg => ({
        'Serial Number': serialNumber++,
        'Team Name': reg.schoolName || 'N/A',
        'Age Group': this.formatAgeCategory(reg.ageCategory || reg.age_category) || 'N/A'
      }));

      return await this.handleExportWithStorage(exportData, 'Draws', 'draws', format);
    } catch (error) {
      console.error('Error exporting draws:', error);
      throw error;
    }
  },

  // ============================================================================
  // EXPORT APPAREL
  // ============================================================================

  async exportApparel(format, filters = {}) {
    try {
      // Fetch ALL registrations with filters
      const result = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
      const registrations = result?.data || [];

      // Fetch players for each registration
      const allApparelData = [];
      let serialNumber = 1;

      for (const reg of registrations) {
        try {
          const players = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
          (players || []).forEach(player => {
            allApparelData.push({
              'Serial Number': serialNumber++,
              'Age Group': this.formatAgeCategory(reg.ageCategory || reg.age_category) || 'N/A',
              'Player Name': `${player.first_name} ${player.middle_name || ''} ${player.last_name}`.trim(),
              'School Name': reg.schoolName || 'N/A',
              'T-Shirt Size': this.formatTshirtSize(player.tshirt_size),
              'Pant Size': this.formatPantSize(player.pant_size)
            });
          });
        } catch (err) {
          console.error(`Error fetching players for registration ${reg.id}:`, err);
        }
      }

      return await this.handleExportWithStorage(allApparelData, 'Apparel', 'apparel', format);
    } catch (error) {
      console.error('Error exporting apparel:', error);
      throw error;
    }
  },

  // ============================================================================
  // EXPORT PLAYER DETAILS (Enhanced)
  // ============================================================================

  async exportPlayerDetails(format, filters = {}) {
    try {
      // Fetch ALL registrations with filters
      const result = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
      const registrations = result?.data || [];

      // Fetch players for each registration
      const allPlayerData = [];

      for (const reg of registrations) {
        try {
          const players = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
          (players || []).forEach(player => {
            allPlayerData.push({
              'Player Name': `${player.first_name} ${player.middle_name || ''} ${player.last_name}`.trim(),
              'School Name': reg.schoolName || 'N/A',
              'Age Group': this.formatAgeCategory(reg.ageCategory || reg.age_category) || 'N/A',
              'Date of Birth': player.date_of_birth || 'N/A',
              'Age': this.calculateAge(player.date_of_birth),
              'Mobile Number': player.mobile_number || 'N/A',
              'Email ID': player.email || 'N/A',
              'T-Shirt Size': this.formatTshirtSize(player.tshirt_size),
              'Pant Size': this.formatPantSize(player.pant_size)
            });
          });
        } catch (err) {
          console.error(`Error fetching players for registration ${reg.id}:`, err);
        }
      }

      return await this.handleExportWithStorage(allPlayerData, 'Player_Details', 'player_details', format);
    } catch (error) {
      console.error('Error exporting player details:', error);
      throw error;
    }
  },

  // ============================================================================
  // EXPORT PLAYER CARDS (Bulk/Selection Based)
  // ============================================================================

  async exportPlayerCards(selectedPlayerIds = [], filters = {}, format = 'PDF') {
    try {
      let players = [];

      if (selectedPlayerIds && selectedPlayerIds.length > 0) {
        // Export selected players only
        for (const playerId of selectedPlayerIds) {
          try {
            const { data: player, error } = await supabase
              .from('players')
              .select(`
                *,
                registrations!inner (
                  id,
                  registration_number,
                  age_category,
                  schools (name)
                )
              `)
              .eq('id', playerId)
              .single();

            if (!error && player) {
              players.push(player);
            }
          } catch (err) {
            console.error(`Error fetching player ${playerId}:`, err);
          }
        }
      } else {
        // Export all players matching filters
        const result = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
        const registrations = result?.data || [];

        for (const reg of registrations) {
          try {
            const regPlayers = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
            (regPlayers || []).forEach(player => {
              players.push({
                ...player,
                registrations: {
                  age_category: reg.ageCategory || reg.age_category,
                  schools: { name: reg.schoolName }
                }
              });
            });
          } catch (err) {
            console.error(`Error fetching players for registration ${reg.id}:`, err);
          }
        }
      }

      if (players.length === 0) {
        alert('No players found to export');
        return;
      }

      // Generate PDF for each player card
      if (format === 'PDF') {
        const jsPDF = (await import('jspdf')).default;

        // Custom card dimensions: 10.2225 cm x 13.2578 cm
        // jsPDF expects sizes in millimeters.
        const CARD_WIDTH_MM = 102.225;
        const CARD_HEIGHT_MM = 132.578;
        
        for (let i = 0; i < players.length; i++) {
          const player = players[i];
          const reg = player.registrations || {};
          const schoolName = reg.schools?.name || 'N/A';
          const ageCategory = this.formatAgeCategory(reg.age_category) || 'N/A';
          
          const pdf = new jsPDF('p', 'mm', [CARD_WIDTH_MM, CARD_HEIGHT_MM]);
          let y = 20;
          const margin = 10;
          const pageWidth = CARD_WIDTH_MM;

          // Title
          pdf.setFontSize(18);
          pdf.setFont(undefined, 'bold');
          pdf.text('Player Card', margin, y);
          y += 15;

          // Player Information
          pdf.setFontSize(12);
          pdf.setFont(undefined, 'bold');
          pdf.text('Player Information:', margin, y);
          y += 8;
          pdf.setFont(undefined, 'normal');
          
          pdf.text(`Name: ${player.first_name} ${player.middle_name || ''} ${player.last_name}`.trim(), margin, y);
          y += 6;
          pdf.text(`School: ${schoolName}`, margin, y);
          y += 6;
          pdf.text(`Age Group: ${ageCategory}`, margin, y);
          y += 6;
          pdf.text(`Mobile: ${player.mobile_number || 'N/A'}`, margin, y);
          y += 6;
          pdf.text(`Email: ${player.email || 'N/A'}`, margin, y);
          y += 6;
          pdf.text(`MCA ID: ${player.mca_id || 'N/A'}`, margin, y);
          y += 6;
          pdf.text(`Role: ${player.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}`, margin, y);
          y += 6;
          pdf.text(`T-Shirt Size: ${this.formatTshirtSize(player.tshirt_size)}`, margin, y);
          y += 6;
          pdf.text(`Pant Size: ${this.formatPantSize(player.pant_size)}`, margin, y);
          y += 6;
          pdf.text(`Date of Birth: ${player.date_of_birth || 'N/A'}`, margin, y);
          y += 6;
          pdf.text(`Captain: ${player.is_captain ? 'Yes' : 'No'}`, margin, y);

          // Save PDF
          const playerName = `${player.first_name}_${player.last_name}`.replace(/\s+/g, '_');
          pdf.save(`PlayerCard_${playerName}_${new Date().getTime()}.pdf`);
        }

        alert(`Successfully exported ${players.length} player card(s)`);
      } else {
        // For CSV/Excel, export as table
        const exportData = players.map(player => {
          const reg = player.registrations || {};
          return {
            'Player Name': `${player.first_name} ${player.middle_name || ''} ${player.last_name}`.trim(),
            'School Name': reg.schools?.name || 'N/A',
            'Age Group': this.formatAgeCategory(reg.age_category) || 'N/A',
            'Mobile Number': player.mobile_number || 'N/A',
            'Email ID': player.email || 'N/A',
            'T-Shirt Size': this.formatTshirtSize(player.tshirt_size),
            'Pant Size': this.formatPantSize(player.pant_size),
            'MCA ID': player.mca_id || 'N/A',
            'Role': player.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A',
            'Date of Birth': player.date_of_birth || 'N/A',
            'Captain': player.is_captain ? 'Yes' : 'No'
          };
        });

        return await this.handleExportWithStorage(exportData, 'Player_Cards', 'player_cards', format);
      }
      // For PDF format with multiple files, return null for file info (multiple files not stored)
      return { filePath: null, fileName: null, publicUrl: null };
    } catch (error) {
      console.error('Error exporting player cards:', error);
      throw error;
    }
  },

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  calculateAge(dateOfBirth) {
    if (!dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age.toString();
  },

  // Format tshirt size enum to readable format
  formatTshirtSize(size) {
    if (!size) return 'N/A';
    const sizeMap = {
      's_36': 'S - 36',
      'm_38': 'M - 38',
      'l_40': 'L - 40',
      'xl_42': 'XL - 42',
      'xxl_44': 'XXL - 44',
      'xxxl_46': 'XXXL - 46',
      'size_26': '26',
      'size_28': '28',
      'size_30': '30',
      'size_32': '32',
      'size_34': '34',
      'size_36': '36',
      'size_38': '38',
      'size_40': '40',
      'size_42': '42',
      'size_44': '44',
      'size_46': '46'
    };
    return sizeMap[size] || size;
  },

  // Format pant size enum to readable format
  formatPantSize(size) {
    if (!size) return 'N/A';
    const sizeMap = {
      'size_24': '24',
      'size_26': '26',
      'size_28': '28',
      'size_30': '30',
      'size_32': '32',
      'size_34': '34',
      'size_36': '36',
      'size_38': '38',
      'size_40': '40'
    };
    return sizeMap[size] || size;
  },

  // Derive gender from age_category
  getGenderFromAgeCategory(ageCategory) {
    if (!ageCategory) return 'N/A';
    if (ageCategory.includes('boys')) return 'Male';
    if (ageCategory.includes('girls')) return 'Female';
    return 'N/A';
  },

  // Format age category to readable format
  formatAgeCategory(ageCategory) {
    if (!ageCategory) return 'N/A';
    const map = {
      'boys_u14': 'Boys U14',
      'girls_u15': 'Girls U15',
      'boys_u16': 'Boys U16'
    };
    return map[ageCategory] || ageCategory.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  },

  // Helper function to handle export: create blob, upload, download, return file info
  async handleExportWithStorage(exportData, filename, exportType, format) {
    let fileBlob;
    const timestamp = new Date().toISOString().split('T')[0];
    let fileName;

    if (format === 'CSV') {
      fileBlob = this.createCSVBlob(exportData, filename);
      fileName = `${filename}_${timestamp}.csv`;
    } else if (format === 'Excel') {
      fileBlob = this.createExcelBlob(exportData, filename);
      fileName = `${filename}_${timestamp}.xlsx`;
    } else if (format === 'PDF') {
      fileBlob = await this.createPDFBlob(exportData, filename);
      fileName = `${filename}_${timestamp}.pdf`;
    } else {
      throw new Error(`Unsupported format: ${format}`);
    }

    // Upload to storage (optional - don't fail if it fails)
    let filePath = null;
    let publicUrl = null;
    let fileSize = fileBlob.size; // Get file size from blob
    try {
      const uploadResult = await this.uploadExportFile(fileBlob, fileName, exportType);
      filePath = uploadResult.filePath;
      publicUrl = uploadResult.publicUrl;
      // Use fileSize from upload result if available, otherwise use blob size
      if (uploadResult.fileSize) {
        fileSize = uploadResult.fileSize;
      }
    } catch (uploadError) {
      console.warn('Failed to upload export file to storage:', uploadError);
      // Continue with download even if upload fails
    }

    // Trigger download
    const link = document.createElement('a');
    const url = URL.createObjectURL(fileBlob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { filePath, fileName, publicUrl, fileSize };
  },

  // Upload export file to Supabase Storage
  async uploadExportFile(fileBlob, fileName, exportType) {
    try {
      // File path within the bucket (without 'exports/' prefix since bucket is already 'exports')
      const filePath = `${exportType}/${Date.now()}_${fileName}`;
      
      const { data, error } = await supabase.storage
        .from('exports') // Make sure this bucket exists in Supabase
        .upload(filePath, fileBlob, {
          contentType: fileName.endsWith('.pdf') ? 'application/pdf' : 
                       fileName.endsWith('.xlsx') ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
                       'text/csv',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('exports')
        .getPublicUrl(filePath);

      // Return full path including bucket name for database storage, along with file size
      return { 
        filePath: `exports/${filePath}`, 
        publicUrl: urlData.publicUrl,
        fileSize: fileBlob.size
      };
    } catch (error) {
      console.error('Error uploading export file:', error);
      // Don't throw - file storage is optional, export can still succeed
      return { filePath: null, publicUrl: null, fileSize: null, error: error.message };
    }
  },

  // Save export history to database (optional)
  async saveExportHistory(exportType, format, filters, recordCount, filePath = null, fileName = null, fileSize = null, status = 'completed') {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('No authenticated user found, skipping export history save');
        return { success: false, error: 'User not authenticated' };
      }

      const insertData = {
        export_type: exportType,
        format: format,
        filters: filters,
        record_count: recordCount,
        exported_by: user.id,
        exported_at: new Date().toISOString(),
        status: status
      };

      // Add file information if provided
      if (filePath) insertData.file_path = filePath;
      if (fileName) insertData.file_name = fileName;
      if (fileSize !== null && fileSize !== undefined) insertData.file_size = fileSize;

      const { error } = await supabase.from('export_history').insert(insertData);

      if (error) {
        console.error('Error saving export history:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error saving export history:', error);
      return { success: false, error: error.message };
    }
  },

  // Get last exported date for a specific export type
  async getLastExportedDate(exportType) {
    try {
      const { data, error } = await supabase
        .from('export_history')
        .select('exported_at')
        .eq('export_type', exportType)
        .order('exported_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.warn('Error fetching last exported date:', error);
        return null;
      }

      return data?.exported_at ? new Date(data.exported_at).toISOString().split('T')[0] : null;
    } catch (error) {
      console.error('Error getting last exported date:', error);
      return null;
    }
  },

  // Get record count for export preview
  async getRecordCount(exportType, filters = {}) {
    try {
      switch (exportType) {
        case 'registration':
          const regResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 1 });
          return regResult?.total || 0;
        
        case 'players':
        case 'apparel':
        case 'player_details':
        case 'player_cards':
          const playersResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 1 });
          const regsForPlayers = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
          let playerCount = 0;
          for (const reg of regsForPlayers?.data || []) {
            try {
              const players = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
              playerCount += (players || []).length;
            } catch (err) {
              console.error(`Error counting players for registration ${reg.id}:`, err);
            }
          }
          return playerCount;
        
        case 'draws':
          const drawsResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 1 });
          return drawsResult?.total || 0;
        
        case 'schools':
          const schoolsResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
          const schoolSet = new Set();
          (schoolsResult?.data || []).forEach(reg => {
            if (reg.schoolName) schoolSet.add(reg.schoolName);
          });
          return schoolSet.size;
        
        case 'documents':
          const docsResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
          let docCount = 0;
          for (const reg of docsResult?.data || []) {
            try {
              const players = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
              for (const player of players || []) {
                const docs = await registrationService.getPlayerDocuments(player.id);
                docCount += (docs || []).length;
              }
            } catch (err) {
              console.error(`Error counting documents for registration ${reg.id}:`, err);
            }
          }
          return docCount;
        
        default:
          return 0;
      }
    } catch (error) {
      console.error('Error getting record count:', error);
      return 0;
    }
  },

  // Get preview data for export
  async getPreviewData(exportType, filters = {}, limit = 3) {
    try {
      switch (exportType) {
        case 'registration':
          const regResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: limit });
          return (regResult?.data || []).map(reg => {
            const dateStr = reg.submittedDate || (reg.created_at ? new Date(reg.created_at).toISOString().split('T')[0] : 'N/A');
            return {
              'Registration Number': reg.id || 'N/A',
              'School': reg.schoolName || 'N/A',
              'City': reg.city || 'N/A',
              'Category': reg.ageCategory || 'N/A',
              'Status': reg.status || 'N/A',
              'Submitted Date': dateStr
            };
          });
        
        case 'players':
          const playersRegResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: limit });
          const previewPlayers = [];
          for (const reg of playersRegResult?.data || []) {
            try {
              const players = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
              (players || []).slice(0, limit - previewPlayers.length).forEach(player => {
                previewPlayers.push({
                  'Player Name': `${player.first_name} ${player.last_name}`.trim(),
                  'DOB': player.date_of_birth || 'N/A',
                  'Team': reg.schoolName || 'N/A',
                  'Role': player.role || 'Player',
                  'MCA ID': player.mca_id || 'N/A'
                });
              });
              if (previewPlayers.length >= limit) break;
            } catch (err) {
              console.error(`Error fetching players for preview:`, err);
            }
          }
          return previewPlayers.slice(0, limit);
        
        case 'draws':
          const drawsResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: limit });
          let serialNum = 1;
          return (drawsResult?.data || []).map(reg => ({
            'Serial Number': serialNum++,
            'Team Name': reg.schoolName || 'N/A',
            'Age Group': this.formatAgeCategory(reg.ageCategory || reg.age_category) || 'N/A'
          }));
        
        case 'apparel':
          const apparelRegResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: limit });
          const previewApparel = [];
          let apparelSerial = 1;
          for (const reg of apparelRegResult?.data || []) {
            try {
              const players = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
              (players || []).slice(0, limit - previewApparel.length).forEach(player => {
                previewApparel.push({
                  'Serial Number': apparelSerial++,
                  'Age Group': this.formatAgeCategory(reg.ageCategory || reg.age_category) || 'N/A',
                  'Player Name': `${player.first_name} ${player.last_name}`.trim(),
                  'School Name': reg.schoolName || 'N/A',
                  'T-Shirt Size': this.formatTshirtSize(player.tshirt_size),
                  'Pant Size': this.formatPantSize(player.pant_size)
                });
              });
              if (previewApparel.length >= limit) break;
            } catch (err) {
              console.error(`Error fetching apparel data for preview:`, err);
            }
          }
          return previewApparel.slice(0, limit);
        
        case 'player_details':
          const playerDetailsRegResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: limit });
          const previewPlayerDetails = [];
          for (const reg of playerDetailsRegResult?.data || []) {
            try {
              const players = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
              (players || []).slice(0, limit - previewPlayerDetails.length).forEach(player => {
                previewPlayerDetails.push({
                'Player Name': `${player.first_name} ${player.last_name}`.trim(),
                  'School Name': reg.schoolName || 'N/A',
                  'Age Group': this.formatAgeCategory(reg.ageCategory || reg.age_category) || 'N/A',
                'Date of Birth': player.date_of_birth || 'N/A',
                'Age': this.calculateAge(player.date_of_birth),
                  'Mobile Number': player.mobile_number || 'N/A',
                  'Email ID': player.email || 'N/A',
                  'T-Shirt Size': this.formatTshirtSize(player.tshirt_size),
                  'Pant Size': this.formatPantSize(player.pant_size)
                });
              });
              if (previewPlayerDetails.length >= limit) break;
            } catch (err) {
              console.error(`Error fetching player details for preview:`, err);
            }
          }
          return previewPlayerDetails.slice(0, limit);
        
        case 'schools':
          const schoolsRegResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
          const schoolMap = {};
          (schoolsRegResult?.data || []).forEach(reg => {
            const schoolName = reg.schoolName || 'Unknown';
            if (!schoolMap[schoolName]) {
              schoolMap[schoolName] = {
                'School Name': schoolName,
                'City': reg.city || 'N/A',
                'Total Teams': 0,
                'Approved Teams': 0
              };
            }
            schoolMap[schoolName]['Total Teams']++;
            if (reg.status === 'approved') schoolMap[schoolName]['Approved Teams']++;
          });
          return Object.values(schoolMap).slice(0, limit);
        
        case 'player_cards':
          // For player_cards, we need to fetch players from registrations matching filters
          const playerCardsRegResult = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: limit });
          const previewPlayerCards = [];
          for (const reg of playerCardsRegResult?.data || []) {
            try {
              const players = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
              (players || []).slice(0, limit - previewPlayerCards.length).forEach(player => {
                previewPlayerCards.push({
                  'Player Name': `${player.first_name} ${player.middle_name || ''} ${player.last_name}`.trim(),
                  'School Name': reg.schoolName || 'N/A',
                  'Age Group': this.formatAgeCategory(reg.ageCategory || reg.age_category) || 'N/A',
                  'Date of Birth': player.date_of_birth || 'N/A',
                  'MCA ID': player.mca_id || 'N/A',
                  'Status': reg.status || 'N/A'
                });
              });
              if (previewPlayerCards.length >= limit) break;
            } catch (err) {
              console.error(`Error fetching players for player cards preview:`, err);
            }
          }
          return previewPlayerCards.slice(0, limit);
        
        default:
          return [];
      }
    } catch (error) {
      console.error('Error getting preview data:', error);
      return [];
    }
  }
};

